/**
 * Import the S4CA registry from a code export.
 *
 * The export is the office's own list, no header, and it is now the authority for what
 * the archive calls each record, where it is filed, when it started, whether it is
 * alumni, where its supporters give, how many partners it is, what is written about it,
 * and — since migration 0031 — *which kind of thing it is*. This page takes that file,
 * shows what it would do to every record, and then does it.
 *
 * "Partner Units" in the title would now be wrong in a way that matters rather than a way
 * that is merely untidy: one file imports all three kinds, and a page that named only the
 * first would leave somebody looking for where training schools and projects are loaded
 * from. Hence "Import Units" on the tab and the plainer wording throughout.
 *
 * The dry run is the whole point. "Replace the registry" sounds like one action and is
 * really four hundred, so nothing is written until somebody has read a plan that names
 * the records being created, the fields being overwritten, the records in the archive the
 * file does not mention, how many would change kind, and — the part that is easy to
 * assume and expensive to get wrong — everything the import leaves alone, because this
 * file has no column for the photographs or the updates.
 *
 * The Worker decides all of it, twice: once to draw this page, and again on the file
 * itself when Import is pressed. Nothing the browser worked out is sent back as input.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../App'
import { api, reportClientError } from '../lib/api'
import { Card, Tile, Badge, Notice, Spinner, Tabs, useToast, fmtInt } from '../components/ui'
import { useCode } from '../lib/terms'
import { Dropzone } from '../components/Dropzone'

/** What each planned outcome is called on screen. */
const VERDICT = {
  create: { label: 'New record', tone: 'ok' },
  update: { label: 'Updated', tone: 'accent' },
  unchanged: { label: 'Already matches', tone: undefined },
}

/** The file's columns, in the archive's words, for the change list. */
const FIELD_LABEL = {
  display_name: 'Name',
  code_and_name: 'Code and name',
  country_id: 'Country',
  initial_year: 'Initial year',
  status: 'Status',
  donate_url: 'Donate link',
  staff_partners: 'Staff partners',
  non_staff_partners: 'Non-staff partners',
  description: 'Description',
  email: 'Email',
  fund_type: 'Fund type',
}

/**
 * A field's before and after, short enough to read on one line.
 *
 * Every other field the file owns is a word, a year or a URL. A description is a
 * paragraph — four hundred characters of it — and printed whole it turns one line of
 * "what will change" into a wall that hides the twenty lines under it.
 *
 * Clipping from the start is not enough, though, and this is the part worth explaining:
 * two versions of a description usually open with the same sentence and differ somewhere
 * in the middle, so the first ninety characters of each are identical and the line reads
 * as a change to nothing. The window is placed at the first character that actually
 * differs instead, a little before it, so what is on screen is the edit. Both versions in
 * full are in the row's title.
 */
const CLIP = 90
const LEAD = 20

const clipPair = (from, to) => {
  const a = String(from ?? '')
  const b = String(to ?? '')
  if (a.length <= CLIP && b.length <= CLIP) return [a, b]
  let i = 0
  while (i < a.length && i < b.length && a[i] === b[i]) i++
  const start = Math.max(0, i - LEAD)
  const window = (s) =>
    (start > 0 ? '…' : '') + s.slice(start, start + CLIP).trimEnd() + (start + CLIP < s.length ? '…' : '')
  return [window(a), window(b)]
}

const shortUrl = (u) => String(u ?? '').replace(/^https?:\/\//, '').replace(/^www\./, '')

/** One field's before and after, written so a blank reads as a blank rather than a gap. */
const Change = ({ change }) => {
  const blank = (v) => v === null || v === ''
  const [from, to] = clipPair(change.from, change.to)
  return (
    <div
      className="small"
      title={`${blank(change.from) ? '(blank)' : change.from}\n\n→\n\n${blank(change.to) ? '(blank)' : change.to}`}
    >
      <span className="subtle">{FIELD_LABEL[change.field] ?? change.field}: </span>
      <span className="subtle">{blank(change.from) ? '—' : from}</span>
      <span aria-hidden="true"> → </span>
      <strong>{blank(change.to) ? '—' : to}</strong>
    </div>
  )
}

export default function SettingsPartnerImport({ user }) {
  const code = useCode()
  const toast = useToast()
  const qc = useQueryClient()

  const [file, setFile] = useState(null)
  const [plan, setPlan] = useState(null)
  const [planning, setPlanning] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState(null)
  const [outcome, setOutcome] = useState(null)
  const [tab, setTab] = useState('changing')

  if (!user.caps.manageSettings) {
    return (
      <PageHeader title="Import Units">
        <Notice tone="warn" icon="lock" title="Not available to your role">
          Importing a registry export rewrites the name, country, initial year, status,
          donate link and fund type on every record it names, so it is kept to owners and
          administrators. You are signed in as <strong>{user.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  const reset = () => {
    setFile(null)
    setPlan(null)
    setOutcome(null)
    setError(null)
    setTab('changing')
  }

  /** Hand the file to the Worker and ask what it would do with it. */
  const choose = async (picked) => {
    const chosen = [...(picked ?? [])].find((f) => f && !f.name.startsWith('.'))
    setOutcome(null)
    setError(null)
    setPlan(null)
    setFile(chosen ?? null)
    if (!chosen) {
      setError('No file was chosen.')
      return
    }
    setPlanning(true)
    try {
      const fd = new FormData()
      fd.append('file', chosen)
      setPlan(await api('partners/import/plan', { method: 'POST', formData: fd }))
    } catch (err) {
      setError(err.message)
      reportClientError('partner-import-plan', err, { filename: chosen.name, bytes: chosen.size })
    } finally {
      setPlanning(false)
    }
  }

  /** Send the same file again, this time to be written. */
  const run = async () => {
    setRunning(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api('partners/import', { method: 'POST', formData: fd })
      setOutcome(res)
      // The registry is behind most of the archive — the directory, the country pages,
      // every picker — and this has just rewritten several hundred rows of it.
      qc.invalidateQueries()
      toast.ok(`${fmtInt(res.created + res.updated)} record${res.created + res.updated === 1 ? '' : 's'} written`)
    } catch (err) {
      setError(err.message)
      reportClientError('partner-import', err, { filename: file?.name, bytes: file?.size })
    } finally {
      setRunning(false)
    }
  }

  const s = plan?.summary
  const rows = plan?.rows ?? []
  const shown =
    tab === 'all' ? rows : tab === 'changing' ? rows.filter((r) => r.verdict !== 'unchanged') : []

  return (
    <PageHeader
      title="Import Units"
      sub="A registry export: one line per partner unit, training school or project, no header"
      actions={
        plan && !running ? (
          <button className="btn" onClick={reset}>
            Choose another file
          </button>
        ) : null
      }
    >
      <div className="col">
        <Notice icon="info" title="How the file is read">
          <p>
            One line per record. The first six columns are always in this order: the{' '}
            <strong>S4CA code</strong>, the <strong>code and name</strong>, the{' '}
            <strong>country or category</strong>, the <strong>initial year</strong>,{' '}
            <strong>Yes</strong> where the record is alumni, and the{' '}
            <strong>donate link</strong>. After those come the{' '}
            <strong>description</strong>, the number of <strong>staff</strong> partners and
            the number of <strong>non-staff</strong> partners. A first line reading
            “S4CA-000, S4CA Name, Country, 0, Yes, URL Link, Description, 0, 0” is the
            export’s legend and is skipped.
          </p>
          <p style={{ marginTop: 8 }}>
            <strong>One file, three kinds of record.</strong> The same S4CA numbering carries
            partner units, training schools and projects, and a{' '}
            <strong>Fund Type</strong> column is how the file says which is which — the
            words <strong>Staff</strong>, <strong>Project</strong> and{' '}
            <strong>Training School</strong> (or <em>Training</em>, <em>School</em>,{' '}
            <em>TS</em>). That one column decides which Register page a record appears on:
            Partner Units, Training Schools or Projects. Everything else on this page
            applies to all three equally, and an update can be filed against any of them.
          </p>
          <p style={{ marginTop: 8 }}>
            Three things can happen with that column, and they are deliberately different.
            A file with <strong>no Fund Type column</strong> leaves every record filed
            exactly where it is — it is not read as saying they are all staff. A{' '}
            <strong>blank cell</strong> in a file that has the column is the file not
            saying: the record keeps the kind it has, and a record being created for the
            first time is a partner unit. A cell holding{' '}
            <strong>something else</strong> — <em>Relief</em>, say — is reported against
            that line and the field left alone rather than guessed at. The plan below
            counts how many records would <strong>change kind</strong>, which is the
            number to check before pressing Import: a column read wrongly shows up there,
            rather than as two empty Register pages afterwards.
          </p>
          <p style={{ marginTop: 8 }}>
            An <strong>Alias Name</strong> and a <strong>Preferred Name</strong> are read as well,
            wherever the legend line names those columns — and they are the one thing here that is
            never shown. They are stored so that somebody who already knows a name can type it
            into any search box and be given the record; they appear on no record, no card, no
            export, and not on this page. The plan below counts them and says nothing more about
            them.
          </p>
          <p style={{ marginTop: 8 }}>
            That legend line is a row of <em>example values</em> rather than a header, so it names
            some columns and not others. The columns past the sixth are found by whichever of
            these works: the legend naming them, then their shape — past the sixth column, a bare number
            is a head count, and a column whose every filled cell reads staff, project or training
            school is the Fund Type — and only then a fixed position. So the Fund Type column is
            found whether its legend cell reads “Fund Type” or simply “Staff”. Both kinds of
            partner count as partners here and the archive shows their sum:
            most units are one person, and a unit of two is usually a married couple serving
            together. A record with both columns empty is left <em>uncounted</em>, which is not the
            same as one recorded as having nobody.
          </p>
          <p style={{ marginTop: 8 }}>
            The file may also carry an <strong>email address</strong> per record, which is what
            the Communication module writes to — name that column <strong>Email</strong> in
            the first line. A cell holding two addresses, as a couple&rsquo;s often does, keeps
            both and writes to each. Addresses are <strong>not shown on the cards or the
            record</strong>: they are readable only under Communication. A file with no such
            column writes none rather than emptying them, and the plan below says which it is.
          </p>
          <p style={{ marginTop: 8 }}>
            Every field above is <strong>replaced</strong> on each record the file names, the
            description included, and a code it names that the archive has not got is created. A
            file with no description column writes no descriptions rather than emptying them, and
            the plan below says which it is. Nothing else on a record is touched: the file has no
            column for the photograph, and no record is deleted — the updates filed against
            them are counted in the plan.
          </p>
        </Notice>

        {!plan && (
          <Dropzone
            onFiles={choose}
            accept=".csv,text/csv,text/plain"
            busy={planning}
            busyLabel="Reading the file…"
            icon="file"
            title="Choose the export file"
          >
            Drop the .csv here, or click to pick it. Nothing is written until you have read the
            plan.
          </Dropzone>
        )}

        {error && (
          <Notice tone="danger" icon="warning" title="That file could not be read">
            {error}
          </Notice>
        )}

        {s && (
          <>
            <div className="grid grid-tiles">
              <Tile label="Partner units in the file" value={fmtInt(s.rows)} note={file?.name} />
              <Tile
                label="To be created"
                value={fmtInt(s.create)}
                tone={s.create ? 'ok' : undefined}
                note={s.create ? 'not in the archive yet' : 'every code is already here'}
              />
              <Tile
                label="To be updated"
                value={fmtInt(s.update)}
                tone={s.update ? 'accent' : undefined}
                note={`${fmtInt(s.unchanged)} already match the file`}
              />
              <Tile
                label="In the archive, not in the file"
                value={fmtInt(s.missing)}
                tone={s.missing_with_newsletters ? 'warn' : undefined}
                note={s.missing ? 'left exactly as they are' : null}
              />
            </div>

            <div className="grid grid-tiles">
              <Tile label="Donate links" value={fmtInt(s.with_donate_url)} note={`${fmtInt(s.without_donate_url)} units have none`} />
              <Tile label="Marked alumni" value={fmtInt(s.alumni)} />
              {/* The head counts, which is the one figure on this page that is about
                  people rather than records — and the one most worth checking against
                  what the office knows before pressing Import. */}
              <Tile
                label="Partners counted"
                value={fmtInt(s.people)}
                note={
                  s.counted
                    ? `${fmtInt(s.staff)} staff · ${fmtInt(s.non_staff)} non-staff` +
                      (s.uncounted ? ` · ${fmtInt(s.uncounted)} units uncounted` : '')
                    : 'this file has no head-count columns'
                }
                tone={s.counted ? 'accent' : undefined}
              />
              <Tile
                label="Units of more than one"
                value={fmtInt(s.couples)}
                note={s.counted ? 'usually a married couple' : null}
              />
              {s.has_description_column ? (
                <Tile
                  label="Descriptions written"
                  value={fmtInt(s.changed.description ?? 0)}
                  tone={s.descriptions_blanked ? 'warn' : s.changed.description ? 'accent' : undefined}
                  note={
                    s.descriptions_blanked
                      ? `${fmtInt(s.descriptions_blanked)} would be cleared — the file has none for them`
                      : `of ${fmtInt(s.described)} in the file; the rest already match`
                  }
                />
              ) : (
                <Tile
                  label="Descriptions kept"
                  value={fmtInt(s.keeps.descriptions)}
                  note="this file has no description column"
                />
              )}
              {/* What kind each row is, and — the number to read — how many records this
                  import would move from one Register page to another. A fund type column
                  read wrongly shows up here as a three-figure `refiled` before anything is
                  written, rather than as two empty pages afterwards. */}
              {s.has_fund_type_column ? (
                <Tile
                  label="Fund types"
                  value={fmtInt(s.fund_types.staff)}
                  tone={s.refiled ? 'warn' : 'accent'}
                  note={
                    `staff · ${fmtInt(s.fund_types.training)} training schools · ` +
                    `${fmtInt(s.fund_types.project)} projects` +
                    (s.refiled ? ` · ${fmtInt(s.refiled)} would change kind` : '')
                  }
                />
              ) : (
                <Tile
                  label="Fund types kept"
                  value="—"
                  note="this file has no fund type column, so every record stays where it is filed"
                />
              )}
              <Tile label="Photographs kept" value={fmtInt(s.keeps.photos)} note={`and ${fmtInt(s.keeps.newsletters)} updates`} />
            </div>

            {plan.messages.map((msg) => (
              <Notice key={msg} icon="info">
                {msg}
              </Notice>
            ))}

            <Card
              title={outcome ? 'What the Import Did' : 'What the Import Will Do'}
              sub={
                outcome
                  ? `${fmtInt(outcome.created)} created, ${fmtInt(outcome.updated)} updated`
                  : `${fmtInt(s.create + s.update)} of ${fmtInt(s.rows)} rows change something`
              }
              actions={
                <button
                  className="btn btn-primary"
                  onClick={run}
                  disabled={running || !!outcome || !(s.create + s.update)}
                >
                  {running
                    ? 'Importing…'
                    : outcome
                      ? 'Imported'
                      : s.create + s.update
                        ? `Import ${fmtInt(s.create + s.update)} record${s.create + s.update === 1 ? '' : 's'}`
                        : 'Nothing to change'}
                </button>
              }
            >
              {running && (
                <div className="row" style={{ marginBottom: 12 }}>
                  <Spinner />
                  <span className="small">Writing the registry…</span>
                </div>
              )}

              <Tabs
                active={tab}
                onChange={setTab}
                tabs={[
                  { key: 'changing', label: 'Changing', count: s.create + s.update },
                  { key: 'all', label: 'Every row', count: s.rows },
                  { key: 'missing', label: 'Not in the file', count: s.missing },
                ]}
              />

              {tab === 'missing' ? (
                <div className="table-wrap">
                  <table className="data compact">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th className="right">Updates</th>
                        <th>What happens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.missing.map((r) => (
                        <tr key={r.s4ca_code}>
                          <td className="small mono">{code(r.s4ca_code)}</td>
                          <td className="small">{r.display_name}</td>
                          <td className="small">
                            {r.status !== 'active' && <Badge>{r.status}</Badge>}
                            {r.is_orphan ? <Badge tone="warn">not in registry</Badge> : null}
                          </td>
                          <td className="num">{fmtInt(r.newsletter_count)}</td>
                          <td className="small subtle">
                            Kept as it is
                            {r.newsletter_count > 0 ? ' — deleting it would delete its updates' : ''}
                          </td>
                        </tr>
                      ))}
                      {!plan.missing.length && (
                        <tr>
                          <td colSpan={5} className="small subtle">
                            Every record in the archive is named in this file.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="data compact">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Country</th>
                        <th>Year</th>
                        <th>Donate</th>
                        <th>Outcome</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((r) => (
                        <tr key={r.code}>
                          <td className="small mono">{r.code}</td>
                          <td className="small">
                            {r.display_name}
                            {r.status === 'alumni' && (
                              <>
                                {' '}
                                <Badge>alumni</Badge>
                              </>
                            )}
                          </td>
                          <td className="small">
                            {r.country_label ?? '—'}
                            {r.country_is_new && (
                              <>
                                {' '}
                                <Badge tone="warn">new</Badge>
                              </>
                            )}
                          </td>
                          <td className="small">{r.initial_year ?? '—'}</td>
                          <td className="small truncate" title={r.donate_url ?? ''}>
                            {r.donate_url ? shortUrl(r.donate_url) : '—'}
                          </td>
                          <td className="small">
                            <Badge tone={VERDICT[r.verdict]?.tone}>
                              {outcome && r.verdict !== 'unchanged'
                                ? r.verdict === 'create'
                                  ? 'Created'
                                  : 'Updated'
                                : VERDICT[r.verdict]?.label}
                            </Badge>
                            {r.verdict === 'update' &&
                              r.changes.map((c) => <Change key={c.field} change={c} />)}
                            {/* A line with no values, because there are none to show: the
                                two private names are stored to be searched and never
                                displayed, and "changed" is the whole of what can be said
                                about them here. */}
                            {r.private_names_changed && (
                              <div className="small subtle">
                                Alias or preferred name: changed (not shown)
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!shown.length && (
                        <tr>
                          <td colSpan={6} className="small subtle">
                            Every row in the file already matches the archive.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}

        {outcome && (
          <Notice icon="check" tone="ok" title={`${fmtInt(outcome.created + outcome.updated)} records written`}>
            <p>
              {fmtInt(outcome.created)} created and {fmtInt(outcome.updated)} updated;{' '}
              {fmtInt(outcome.unchanged)} already matched the file.
              {outcome.countries.length
                ? ` ${outcome.countries.join(', ')} ${outcome.countries.length === 1 ? 'was' : 'were'} added to the countries list.`
                : ''}
            </p>
            <p style={{ marginTop: 8 }}>
              {fmtInt(outcome.missing.length)} record
              {outcome.missing.length === 1 ? '' : 's'} in the archive
              {outcome.missing.length === 1 ? ' was' : ' were'} not in the file and{' '}
              {outcome.missing.length === 1 ? 'was' : 'were'} left alone. Photographs and
              updates were not written.
            </p>
          </Notice>
        )}
      </div>
    </PageHeader>
  )
}
